/**
 * Arbitrary-precision decimal arithmetic for the formula engine.
 *
 * This is deliberately NOT the same thing as `apps/api/src/common/decimal.ts`.
 * That module works at a fixed scale of four places, because that is exactly
 * what NUMERIC(19,4) holds and its job is to add up values that came out of a
 * SQL aggregate. A spreadsheet cannot work that way: the moment the user types
 * `=A1/3` a fixed four-place type either truncates or lies, and a client who
 * checks his own arithmetic on a calculator will catch it.
 *
 * A value is `u / 10^s` — an integer significand and a decimal scale. All
 * arithmetic is exact except division, which is the only operation that can
 * produce a non-terminating result and so is the only one that rounds.
 *
 * Floats are never involved at any point.
 */
/**
 * Decimal places kept for division. Excel carries 15 significant digits;
 * going wider costs nothing here and means a chain of divisions does not
 * visibly drift before the final rounding.
 */
export const DIVISION_SCALE = 20;
const TEN = 10n;
function pow10(n) {
    return TEN ** BigInt(n);
}
export const ZERO = { u: 0n, s: 0 };
export const ONE = { u: 1n, s: 0 };
/** Drop trailing decimal zeros so `1.500` and `1.5` compare and print alike. */
export function normalise(d) {
    let { u, s } = d;
    if (u === 0n)
        return ZERO;
    while (s > 0 && u % TEN === 0n) {
        u /= TEN;
        s -= 1;
    }
    return { u, s };
}
/** Restate both operands at a common scale so their significands line up. */
function align(a, b) {
    if (a.s === b.s)
        return { au: a.u, bu: b.u, s: a.s };
    const s = Math.max(a.s, b.s);
    return { au: a.u * pow10(s - a.s), bu: b.u * pow10(s - b.s), s };
}
export function fromInt(value) {
    return { u: BigInt(value), s: 0 };
}
/**
 * Parse a decimal literal. Accepts an optional sign, digits with an optional
 * fractional part, and scientific notation (`1.5e3`), which is what a
 * spreadsheet import will hand us for large numbers.
 *
 * Returns null rather than throwing: in a spreadsheet, "not a number" is an
 * ordinary outcome for a cell containing text, not an error condition.
 */
export function fromString(raw) {
    const text = raw.trim();
    if (!text)
        return null;
    const match = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(text);
    if (!match)
        return null;
    const [, sign = '', whole = '', fraction = '', exponent] = match;
    if (!whole && !fraction)
        return null; // a lone "." or "+"
    const digits = `${whole}${fraction}`;
    let u = BigInt(digits);
    let s = fraction.length;
    if (exponent) {
        const shift = Number(exponent);
        if (shift > 0) {
            // Consume the fractional scale first, then grow the significand.
            const consumed = Math.min(shift, s);
            s -= consumed;
            const remainder = shift - consumed;
            if (remainder > 0)
                u *= pow10(remainder);
        }
        else if (shift < 0) {
            s += -shift;
        }
    }
    if (sign === '-')
        u = -u;
    return normalise({ u, s });
}
export function toString(d) {
    const { u, s } = normalise(d);
    if (s === 0)
        return u.toString();
    const negative = u < 0n;
    const abs = (negative ? -u : u).toString().padStart(s + 1, '0');
    const whole = abs.slice(0, abs.length - s);
    const fraction = abs.slice(abs.length - s);
    return `${negative ? '-' : ''}${whole}.${fraction}`;
}
export function add(a, b) {
    const { au, bu, s } = align(a, b);
    return normalise({ u: au + bu, s });
}
export function subtract(a, b) {
    const { au, bu, s } = align(a, b);
    return normalise({ u: au - bu, s });
}
export function multiply(a, b) {
    return normalise({ u: a.u * b.u, s: a.s + b.s });
}
export function negate(a) {
    return { u: -a.u, s: a.s };
}
export function abs(a) {
    return a.u < 0n ? { u: -a.u, s: a.s } : a;
}
export function isZero(a) {
    return a.u === 0n;
}
export function compare(a, b) {
    const { au, bu } = align(a, b);
    return au < bu ? -1 : au > bu ? 1 : 0;
}
/**
 * Integer division of significands, rounding half away from zero — the rule
 * users expect, and the one Excel applies. Banker's rounding is better for
 * repeated statistical work and worse for someone reconciling an invoice by
 * hand, which is the job here.
 */
function divideRounded(numerator, denominator) {
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = n / d;
    const twiceRemainder = (n % d) * 2n;
    const rounded = twiceRemainder >= d ? quotient + 1n : quotient;
    return negative ? -rounded : rounded;
}
/** Returns null on division by zero — the caller turns that into `#DIV/0!`. */
export function divide(a, b, scale = DIVISION_SCALE) {
    if (b.u === 0n)
        return null;
    // a/b at scale S = (a.u * 10^(b.s + S)) / (b.u * 10^a.s)
    const numerator = a.u * pow10(b.s + scale);
    const denominator = b.u * pow10(a.s);
    return normalise({ u: divideRounded(numerator, denominator), s: scale });
}
/** Round to `places` decimals, half away from zero. Negative places round to
 *  tens, hundreds and so on, matching ROUND(123, -2) = 100. */
export function round(a, places) {
    if (places >= a.s)
        return normalise(a);
    if (places >= 0) {
        const drop = a.s - places;
        return normalise({ u: divideRounded(a.u, pow10(drop)), s: places });
    }
    const factor = pow10(-places);
    const whole = divideRounded(a.u, pow10(a.s));
    return normalise({ u: divideRounded(whole, factor) * factor, s: 0 });
}
/** Truncate toward zero. */
export function truncate(a, places = 0) {
    if (places >= a.s)
        return normalise(a);
    const drop = a.s - places;
    return normalise({ u: a.u / pow10(drop), s: Math.max(places, 0) });
}
export function floor(a) {
    const t = truncate(a);
    return a.u < 0n && compare(t, a) !== 0 ? subtract(t, ONE) : t;
}
export function ceil(a) {
    const t = truncate(a);
    return a.u > 0n && compare(t, a) !== 0 ? add(t, ONE) : t;
}
/**
 * Raise to an integer power. Fractional exponents would need irrational
 * results, which an exact decimal cannot represent; the evaluator reports
 * those as #NUM! rather than quietly falling back to floating point.
 */
export function power(base, exponent) {
    const whole = normalise(exponent);
    if (whole.s !== 0)
        return null;
    let n = whole.u;
    if (n === 0n)
        return ONE;
    const invert = n < 0n;
    if (invert)
        n = -n;
    if (n > 1000n)
        return null; // runaway significand; not a real spreadsheet case
    let result = ONE;
    for (let i = 0n; i < n; i += 1n)
        result = multiply(result, base);
    return invert ? divide(ONE, result) : result;
}
/** Convert to a JS number. For display sizing and chart-like uses only —
 *  never route money through this. */
export function toNumber(a) {
    return Number(toString(a));
}
//# sourceMappingURL=decimal.js.map